/* eslint-disable  @typescript-eslint/no-explicit-any */
/* eslint-disable  @typescript-eslint/no-unused-vars */

import { Search, X } from "lucide-react";
import {
  getLocalStorageUser,
  getSearchStatus,
  updateSearchStatus,
} from "src/helpers/api/sharedFunction";
import React, {
  Dispatch,
  RefObject,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import moment from "moment-timezone";
import { fetchWrapper, StockTableData } from "src/helpers";
import { FiltersFieldProps } from "src/components/dashboard/Filters";
import { useRouter } from "next/router";

export interface Company {
  symbol: string;
  company_name: string;
  is_disable?: boolean;
  is_nifty750?: boolean;
}
type SearchContextType = {
  selectedCompany: Company;
  setSelectedCompany: React.Dispatch<React.SetStateAction<Company>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  showHighProbabilityAlertModal: (
    stockName: string,
    isFilteredOut?: boolean,
  ) => void;
  showPlanNotAvailableModal: () => void;
  showFreeRestrictionModal: () => void;
  selectedDate: Date | null;
  setSelectedDate: React.Dispatch<React.SetStateAction<Date | null>>;
  markUserSearched?: () => void;
  appliedFiltersStore: FiltersFieldProps;
  setStoreAppliedFilters: (filters: FiltersFieldProps) => void;
  clearStoreAppliedFilters: () => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  sectorPanelFilters: SectorPanelFilters;
  setSectorPanelFilters: (filters: SectorPanelFilters) => void;
  clearSectorPanelFilters: () => void;
  selectedMonth: number;
  setSelectedMonth: (month: number) => void;
  records: StockTableData[];
  setRecords: React.Dispatch<React.SetStateAction<StockTableData[]>>;
  isFiltersLoaded: boolean;
};

interface SharedSeachProps {
  searchRef: RefObject<HTMLInputElement | null>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  getSelectedCompanyData: (search: string) => Promise<void>;
  setAutoCompelte: Dispatch<SetStateAction<boolean>>;
  handleOnChangeSearchInput: (value: string) => Promise<void>;
  defaultValue: Company;
  autoCompelete: boolean;
  filteredData: any;
}

export interface SectorPanelFilters {
  deselectedSectors: string[];
  selectedSegment: string;
  selectedYears: string[];
  timeSeries: string;
  isDerivative: boolean;
}

export const SearchContext = React.createContext<SearchContextType>({
  selectedCompany: { symbol: "", company_name: "", is_disable: false },
  setSelectedCompany: () => {},
  setIsLoading: () => {},
  isLoading: true,
  showHighProbabilityAlertModal: () => {},
  showPlanNotAvailableModal: () => {},
  showFreeRestrictionModal: () => {},
  selectedDate: null,
  setSelectedDate: () => {},
  markUserSearched: () => {},
  appliedFiltersStore: {
    sector: [],
    near200SMA: [],
    fibLevel: [],
    segment: [],
    stockTrend: [],
    machineLogic: "",
    propLogic: "",
    risk_profile: [],
  },
  setStoreAppliedFilters: () => {},
  clearStoreAppliedFilters: () => {},
  activeTab: "Derivatives",
  setActiveTab: () => {},
  sectorPanelFilters: {
    deselectedSectors: [],
    selectedSegment: "All",
    selectedYears: [],
    timeSeries: "15",
    isDerivative: false,
  },
  setSectorPanelFilters: () => {},
  clearSectorPanelFilters: () => {},
  selectedMonth: new Date().getMonth(),
  setSelectedMonth: () => {},
  records: [],
  setRecords: () => {},
  isFiltersLoaded: false,
});

export const SearchElement = () => {
  const user = getLocalStorageUser();
  const token = user.token;
  const searchContext = useContext(SearchContext);
  const {
    setSelectedCompany,
    selectedDate,
    showHighProbabilityAlertModal,
    showPlanNotAvailableModal,
    showFreeRestrictionModal,
    markUserSearched,
    records,
  } = searchContext;
  const [search, setSearch] = useState("");
  const [autoCompelete, setAutoCompelte] = useState<boolean>(false);
  const [filteredData, setFilterdData] = useState<Company[]>();
  const searchRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const router = useRouter();
  const path = router?.pathname || "";

  const storedDate =
    typeof window !== "undefined" ? localStorage.getItem("selectedDate") : null;
  const dateToUse = selectedDate
    ? moment(selectedDate).tz("Asia/Kolkata").format("YYYY-MM-DD")
    : storedDate;

  const validateSearchLimit = async (symbol: string) => {
    try {
      const res = await fetchWrapper.post(
        "/api/users/search",
        { symbol },
        { Authorization: `Bearer ${token}` },
      );

      if (!res.success && res.message === "Search limit reached") {
        return { allowed: false };
      }

      return { allowed: true };
    } catch (e) {
      return { allowed: false };
    }
  };

  const getCompanyInfo = async (stockName: string) => {
    const response = await fetchWrapper.get(
      "/api/dashboard/company_info?name=" +
        encodeURIComponent(stockName) +
        "&date=" +
        dateToUse,
      undefined,
      {
        Authorization: `Bearer ${token}`,
      },
    );
    return response;
  };

  const getSelectedCompanyData = async (stockName: string) => {
    const filters = searchContext?.appliedFiltersStore || {};
    const activeFilters = Object.entries(filters)
      .filter(([_, value]) => {
        if (Array.isArray(value)) return value.length > 0;
        return value !== "" && value !== null && value !== undefined;
      })
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return `${key}=${value.map((v) => encodeURIComponent(v)).join(",")}`;
        }
        return `${key}=${encodeURIComponent(value)}`;
      })
      .join("&");

    const response = await fetchWrapper.get(
      "/api/dashboard/search?symbol=" +
        encodeURIComponent(stockName) +
        "&date=" +
        dateToUse +
        "&" +
        activeFilters,
      undefined,
      {
        Authorization: `Bearer ${token}`,
      },
    );
    return response;
  };

  const handleOnChangeSearchInput = async (search: string) => {
    const response = await getCompanyInfo(search);
    const uniqueStocks = Array.from(
      new Map(
        response.data.map((stock: Company) => [stock.symbol, stock]),
      ).values(),
    ) as Company[];
    setFilterdData(uniqueStocks.reverse());
  };

  const normalFlow = async (company: Company) => {
    markUserSearched?.();

    if (path === "/dashboard") {
      const existingStock = records.find((r) => r.symbol === company.symbol);
      if (existingStock) {
        setSelectedCompany({ ...company });
        setSearch("");
        setAutoCompelte(false);
        return;
      }

      const response = await getSelectedCompanyData(company.symbol);

      if (response.data) {
        setSelectedCompany(company);
      } else if (response.available_without_filter) {
        showHighProbabilityAlertModal(company.symbol, true);
      } else if (user.plan !== "Nifty 750" && response.is_nifty750) {
        showPlanNotAvailableModal();
      } else {
        showHighProbabilityAlertModal(company.symbol, false);
      }
    } else {
      if (user.plan !== "Nifty 750" && company.is_nifty750) {
        showPlanNotAvailableModal();
      } else {
        setSelectedCompany(company);
      }
    }

    setSearch("");
    setAutoCompelte(false);
  };

  const handleOnSelectCompany = async (company: Company) => {
    if (!user?.is_trial) return normalFlow(company);

    const symbol = company.symbol;
    let status = getSearchStatus();

    const today = new Date().toISOString().split("T")[0];
    if (status.date !== today) {
      status = {
        date: today,
        searchedSymbols: [],
      };
      localStorage.setItem("searchStatus", JSON.stringify(status));
    }
    if (status.searchedSymbols.includes(symbol)) {
      return normalFlow(company);
    }
    const validation = await validateSearchLimit(symbol);

    if (!validation.allowed) {
      showFreeRestrictionModal();
      return;
    }
    updateSearchStatus({
      searchedSymbols: [...status.searchedSymbols, symbol],
    });

    return normalFlow(company);
  };

  useEffect(() => {
    if (search.length >= 2) {
      setAutoCompelte(true);
      setFilterdData(undefined);

      if (debounceRef.current) clearTimeout(debounceRef.current);

      debounceRef.current = setTimeout(() => {
        handleOnChangeSearchInput(search);
      }, 500);
    } else {
      setAutoCompelte(false);
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <div
      className="m-w-[298px] w-full md:flex-1 md:max-w-lg mx-2 mt-3 md:mt-0 flex-1"
      ref={searchRef}
    >
      <div className="relative w-full max-w-full">
        <input
          type="text"
          ref={searchRef}
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && filteredData?.[0]) {
              handleOnSelectCompany(filteredData[0]);
            }
          }}
          placeholder="Search stocks..."
          className="w-full pl-4 pr-10 py-2 bg-[#edf1fc] dark:bg-transparent border border-[#a0a5bb] rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent dark:text-white placeholder-gray-600 dark:placeholder-gray-400 text-sm font-figtree"
          aria-label="Search stocks"
          aria-autocomplete="list"
          aria-controls="search-suggestions"
        />
        {search && (
          <button
            onClick={() => {
              setSearch("");
              setAutoCompelte(false);
            }}
            className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />

        {/* Suggestions dropdown */}
        {autoCompelete && filteredData && filteredData.length > 0 && (
          <ul
            id="search-suggestions"
            className="absolute z-50 w-full max-h-[250px] overflow-y-auto mt-1 bg-white dark:bg-[#2D2D3A] border border-gray-200 dark:border-gray-700 rounded-md shadow-lg overflow-hidden"
            role="listbox"
          >
            {filteredData.map((stock, index) => (
              <li
                key={`${stock.symbol}-${index}`}
                className={`px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex justify-between items-center`}
                onMouseDown={() => {
                  if (!stock.is_disable) {
                    handleOnSelectCompany(stock);
                  }
                }}
                title={stock.company_name}
                role="option"
                aria-selected={index === 0}
              >
                <span
                  className={`font-bold ${stock.is_disable ? "text-gray-200" : "text-gray-900 dark:text-white"}`}
                >
                  {stock.symbol}
                </span>

                <span
                  className={`text-sm truncate ml-2 ${stock.is_disable ? "text-gray-200" : "text-gray-500 dark:text-gray-400"}`}
                >
                  {stock.company_name}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export const SharedSearchedElement = (props: SharedSeachProps) => {
  const {
    searchRef,
    search,
    setAutoCompelte,
    setSearch,
    handleOnChangeSearchInput,
    defaultValue,
    filteredData,
    autoCompelete,
    getSelectedCompanyData,
  } = props;
  return (
    <div className="w-full">
      <div className="relative w-full max-w-full">
        <div className="relative">
          <input
            type="text"
            placeholder="Search stocks..."
            ref={searchRef}
            value={search || ""}
            onChange={(event) => {
              setSearch(event.target.value);
              if (event.target.value.length >= 2) {
                setAutoCompelte(true);
                handleOnChangeSearchInput(event.target.value);
              } else {
                setAutoCompelte(false);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && filteredData?.[0]) {
                const stock = filteredData[0];
                setSearch(stock.symbol);
                getSelectedCompanyData(stock.symbol);
                setAutoCompelte(false);
              }
            }}
            className="w-full pl-4 pr-10 py-2 bg-[#edf1fc] dark:bg-transparent border border-[#a0a5bb] rounded-md focus:outline-none focus-visible:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/40 focus:ring-inset dark:text-white placeholder-gray-600 dark:placeholder-gray-400 text-sm font-figtree"
            aria-label="Search stocks"
            aria-autocomplete="list"
            aria-controls="search-suggestions"
          />

          {/* Search Icon */}
          {!search && (
            <svg
              className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 20"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
              />
            </svg>
          )}

          {/* Clear (X) Button */}
          {search && (
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              onClick={() => {
                setSearch("");
                handleOnChangeSearchInput("");
                setAutoCompelte(false);
              }}
            >
              ✕
            </button>
          )}
        </div>

        {/* Autocomplete Dropdown */}
        {autoCompelete && filteredData && filteredData.length > 0 && (
          <ul
            id="search-suggestions"
            className="absolute z-50 overflow-y-auto max-h-[250px] w-full mt-1 bg-white dark:bg-[#2D2D3A] border border-gray-200 dark:border-gray-700 rounded-md shadow-lg overflow-hidden"
            role="listbox"
          >
            {filteredData.map(
              (
                stock: { symbol: string; company_name: string },
                index: number,
              ) => (
                <li
                  key={`${stock.symbol}-${index}`}
                  className="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex justify-between items-center"
                  onMouseDown={() => {
                    setSearch(stock?.symbol);
                    getSelectedCompanyData(stock.symbol);
                    setAutoCompelte(false);
                  }}
                  role="option"
                  aria-selected={index === 0}
                >
                  <span className="font-bold text-gray-900 dark:text-white">
                    {stock.symbol}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 truncate ml-2">
                    {stock.company_name}
                  </span>
                </li>
              ),
            )}
          </ul>
        )}
      </div>
    </div>
  );
};
